import { DeepPartial, UnpackNestedValue } from 'react-hook-form'

import { OptionI } from '@/utils/forms/optionBuilders'

interface UseFacilitySelectAllParamsIProps {
  watch: <T = any>(fieldName: string) => T
  setValue: <T = any>(
    fieldName: string,
    value: UnpackNestedValue<DeepPartial<T>>,
    options?: { shouldValidate?: boolean; shouldDirty?: boolean }
  ) => void
  facilityOptions: OptionI[]
  fieldName?: string
}

interface UseFacilitySelectAllReturnIProps {
  areAllFacilitiesSelected: boolean
  handleSelectAllFacilities: () => void
}

/**
 * Custom hook to handle "Select All" / "Deselect All" functionality for facility selection
 *
 * @param watch - React Hook Form watch function
 * @param setValue - React Hook Form setValue function
 * @param facilityOptions - Array of available facility options
 * @param fieldName - Name of the form field (defaults to 'facilities')
 * @returns Object containing allFacilitiesSelected boolean and handleSelectAllFacilities function
 */
export const useFacilitySelectAll = ({
  watch,
  setValue,
  facilityOptions,
  fieldName = 'facilities',
}: UseFacilitySelectAllParamsIProps): UseFacilitySelectAllReturnIProps => {
  const selectedFacilities = watch(fieldName) || []

  const selectedIds = new Set(
    selectedFacilities.map((facility: OptionI) => facility.value)
  )
  const areAllFacilitiesSelected =
    facilityOptions.length > 0 &&
    selectedIds.size === facilityOptions.length &&
    facilityOptions.every((option) => selectedIds.has(option.value))

  const handleSelectAllFacilities = () => {
    if (areAllFacilitiesSelected) {
      // Deselect all
      setValue(fieldName, [], {
        shouldValidate: true,
        shouldDirty: true,
      })
    } else {
      // Select all available facilities
      setValue(fieldName, facilityOptions, {
        shouldValidate: true,
        shouldDirty: true,
      })
    }
  }

  return {
    areAllFacilitiesSelected,
    handleSelectAllFacilities,
  }
}
